import api from './api';
import authService from './authService';
import { getValidTenantId } from '../utils/tenantUtils';

// Define route prefixes for Stripe endpoints
const STRIPE_PREFIX = '/v1/stripe';  // Remove the /api prefix
const SUPERADMIN_STRIPE_PREFIX = '/v1/superadmin/stripe';  // Remove the /api prefix

/**
 * Consolidated service to handle all Stripe API requests
 * Includes both SuperAdmin and regular tenant operations
 */
class StripeService {
  /**
   * Get authentication headers with JWT token
   * @returns {Object} Headers object with authorization token
   */
  getAuthHeaders() {
    const token = authService.getToken();
    
    if (!token) {
      console.warn('No authentication token found!');
    }
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Add both Authorization and x-auth-token headers for compatibility
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      headers['x-auth-token'] = token;
    }
    
    return { headers };
  }

  /**
   * Get authentication headers with JWT token and tenant ID
   * @returns {Object} Headers object with authorization token and tenant ID
   */
  getHeadersWithTenant() {
    const headers = this.getAuthHeaders().headers;
    const tenantId = getValidTenantId();
    
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    } else {
      console.warn('No tenant ID found when making Stripe API request!');
    }
    
    return { headers };
  }

  // =============================================
  // PLATFORM ADMIN OPERATIONS (SUPER ADMIN)
  // =============================================

  /**
   * Get all subscriptions
   * @param {Object} filters - Optional filter parameters
   * @returns {Promise<Object>} All subscriptions with pagination
   */
  async getAllSubscriptions(filters = {}) {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/subscriptions`,
        {
          ...this.getAuthHeaders(),
          params: filters
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  }

  /**
   * Get subscription by ID
   * @param {string} id - Subscription ID
   * @returns {Promise<Object>} Subscription details
   */
  async getSubscriptionById(id) {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/subscriptions/${id}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching subscription ${id}:`, error);
      throw error;
    }
  }

  /**
   * Cancel a subscription
   * @param {string} id - Subscription ID
   * @returns {Promise<Object>} Canceled subscription
   */
  async cancelSubscription(id) {
    try {
      const response = await api.post(
        `${SUPERADMIN_STRIPE_PREFIX}/subscriptions/${id}/cancel`,
        {},
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error canceling subscription ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get subscription analytics and stats
   * @returns {Promise<Object>} Subscription statistics
   */
  async getSubscriptionStats() {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/subscriptions/stats`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
      throw error;
    }
  }

  /**
   * Get tenant subscription details
   * @param {String} tenantId - The tenant ID
   * @returns {Promise<Object>} Tenant subscription details
   */
  async getTenantSubscription(tenantId) {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/tenants/${tenantId}/subscription`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching tenant subscription: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get tenant usage statistics
   * @param {String} tenantId - The tenant ID
   * @returns {Promise<Object>} Tenant usage statistics
   */
  async getTenantUsage(tenantId) {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/tenants/${tenantId}/usage`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching tenant usage: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get available plans for tenant
   * @param {String} tenantId - The tenant ID
   * @returns {Promise<Object>} Available plans for tenant
   */
  async getAvailablePlansForTenant(tenantId) {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/tenants/${tenantId}/available-plans`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching available plans for tenant: ${error.message}`);
      throw error;
    }
  }

  /**
   * Change tenant subscription plan
   * @param {String} tenantId - The tenant ID
   * @param {Object} planData - Plan change data {plan, note}
   * @returns {Promise<Object>} Updated subscription
   */
  async changeTenantPlan(tenantId, planData) {
    try {
      console.log(`Changing tenant ${tenantId} plan to ${planData.plan}`);
      
      // Convert from planData.plan to planId for backend API compatibility
      const requestData = {
        planId: planData.plan, // Backend expects planId, not plan
        note: planData.note || ''
      };
      
      const response = await api.patch(
        `${SUPERADMIN_STRIPE_PREFIX}/tenants/${tenantId}/subscription`,
        requestData,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating tenant plan: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get platform-wide payment statistics
   * @returns {Promise<Object>} Promise with payment stats
   */
  async getPaymentStats() {
    try {
      const response = await api.get(
        `${STRIPE_PREFIX}/platform/stats`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching payment stats:', error);
      throw error;
    }
  }

  /**
   * Get all platform plans - pull actual plans from SuperAdmin API for use in Admin portal
   * @returns {Promise<Object>} Promise with plans data
   */
  async getPlatformPlans() {
    try {
      console.log('Fetching platform plans from real API...');
      
      // Get the last deleted plan ID
      const lastDeletedPlanId = localStorage.getItem('lastDeletedPlanId');
      const lastDeletedTimestamp = localStorage.getItem('lastDeletedTimestamp');
      const now = Date.now();
      
      // Add cache busting parameter if we've deleted a plan recently
      const cacheParam = lastDeletedPlanId && lastDeletedTimestamp && 
                        (now - parseInt(lastDeletedTimestamp)) < 5 * 60 * 1000 
                        ? `&_cache=${now}` : '';
      
      // Make request without timeout to avoid artificial delays
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/plans?active=true${cacheParam}`,
        this.getAuthHeaders()
      );
      
      console.log('Raw platform plans response:', response);
      
      // Handle different response formats
      if (response && response.data) {
        // Success format 1: {success: true, data: [...]}
        if (response.data.success === true && Array.isArray(response.data.data)) {
          console.log(`Retrieved ${response.data.data.length} plans from SuperAdmin API`);
          return {
            success: true,
            message: 'Plans retrieved successfully',
            data: response.data.data,
            source: 'api'
          };
        } 
        // Success format 2: {data: [...]}
        else if (Array.isArray(response.data.data)) {
          console.log(`Retrieved ${response.data.data.length} plans (format 2)`);
          return {
            success: true,
            message: 'Plans retrieved successfully',
            data: response.data.data,
            source: 'api'
          };
        }
        // Success format 3: direct array response
        else if (Array.isArray(response.data)) {
          console.log(`Retrieved ${response.data.length} plans (direct array format)`);
          return {
            success: true,
            message: 'Plans retrieved successfully',
            data: response.data,
            source: 'api'
          };
        }
        // Other potential formats
        else if (response.data.plans && Array.isArray(response.data.plans)) {
          console.log(`Retrieved ${response.data.plans.length} plans (nested format)`);
          return {
            success: true,
            message: 'Plans retrieved successfully',
            data: response.data.plans,
            source: 'api'
          };
        }
      }
      
      // If we reach here, provide default plans for unexpected response formats
      console.warn('Unexpected response format, returning default plans');
      return {
        success: true,
        message: 'Using default plans due to API format issues',
        data: this.getDefaultPlans(),
        source: 'default'
      };
    } catch (error) {
      console.error('Error fetching platform plans:', error);
      
      // Detailed error logging for debugging
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
      
      // Check for specific errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        return {
          success: false,
          message: 'Authentication error. Please login again.',
          data: this.getDefaultPlans(),
          source: 'default',
          error: error.message,
          authError: true
        };
      }
      
      // Check for not implemented API
      if (error.response?.status === 404 || error.response?.status === 501) {
        return {
          success: false,
          message: 'This API endpoint is not implemented on the server.',
          data: this.getDefaultPlans(),
          source: 'default',
          error: error.message,
          notImplemented: true
        };
      }
      
      // Return default plans instead of throwing an error to keep the UI working
      return {
        success: true,
        message: `Using default plans due to API error: ${error.message}`,
        data: this.getDefaultPlans(),
        source: 'default',
        error: error.message
      };
    }
  }

  /**
   * Get plans available for admin subscription
   * This fetches from SuperAdmin plans but formats them for admin consumption
   * @returns {Promise<Object>} Promise with available admin plans 
   */
  async getAdminSubscriptionPlans() {
    try {
      console.log('Fetching admin subscription plans...');
      
      // Use the admin-specific endpoint that allows both admin and superadmin roles
      const response = await api.get(
        `${STRIPE_PREFIX}/admin/available-plans`,
        this.getAuthHeaders()
      );
      
      console.log('Admin subscription plans response:', response);
      
      if (response && response.data && response.data.success && Array.isArray(response.data.data)) {
        console.log(`Retrieved ${response.data.data.length} plans for admin subscription`);
        
        // Filter only active plans
        const activePlans = response.data.data.filter(plan => plan.active !== false);
        
        return {
          success: true,
          data: activePlans,
          source: response.data.source
        };
      } else if (response && response.data && Array.isArray(response.data)) {
        // Direct array format
        console.log(`Retrieved ${response.data.length} plans for admin subscription (direct array)`);
        const activePlans = response.data.filter(plan => plan.active !== false);
        
        return {
          success: true,
          data: activePlans,
          source: 'api'
        };
      }
      
      // Fall back to default plans if needed
      return {
        success: true,
        message: 'Using default plans for admin subscription',
        data: this.getDefaultPlans(),
        source: 'default'
      };
    } catch (error) {
      console.error('Error fetching admin subscription plans:', error);
      return {
        success: false,
        message: `Error fetching subscription plans: ${error.message}`,
        data: this.getDefaultPlans()
      };
    }
  }

  /**
   * Get default plans when API fails
   * @returns {Array} Default plans
   */
  getDefaultPlans() {
    return [
      {
        id: 'plan_basic',
        name: 'Basic Plan',
        description: 'Perfect for small organizations just getting started',
        planId: 'basic',
        price: 99,
        interval: 'month',
        features: [
          'Up to 50 members',
          '5GB storage',
          'Basic analytics',
          'Standard support'
        ],
        active: true
      },
      {
        id: 'plan_professional',
        name: 'Professional Plan',
        description: 'Advanced features for growing organizations',
        planId: 'professional',
        price: 299,
        interval: 'month',
        features: [
          'Up to 500 members',
          '20GB storage',
          'Advanced analytics',
          'Priority support',
          'Custom branding'
        ],
        recommended: true,
        active: true
      },
      {
        id: 'plan_enterprise',
        name: 'Enterprise Plan',
        description: 'Complete solution for large organizations',
        planId: 'enterprise',
        price: 499,
        interval: 'month',
        features: [
          'Up to 2000 members',
          '100GB storage',
          'Enterprise analytics',
          'Dedicated support',
          'Custom branding',
          'API access',
          'Advanced security',
          'Priority feature requests'
        ],
        active: true
      }
    ];
  }

  /**
   * Get all platform plans with analytics
   * @returns {Promise<Object>} Plans with usage data
   */
  async getPlatformPlansWithAnalytics() {
    try {
      const [plansResponse, analyticsResponse] = await Promise.all([
        api.get(
          `${STRIPE_PREFIX}/platform/plans`,
          this.getAuthHeaders()
        ),
        api.get(
          `${STRIPE_PREFIX}/platform/plans/analytics`,
          this.getAuthHeaders()
        )
      ]);
      
      const plans = plansResponse.data.data;
      const analytics = analyticsResponse.data.data;
      const enhancedPlans = plans.map(plan => {
        const planAnalytics = analytics.tenantsByPlan.find(item => item.planId === plan.planId) || { count: 0 };
        const revenueAnalytics = analytics.revenueByPlan.find(item => item.planId === plan.planId) || { revenue: 0 };
        return {
          ...plan,
          tenantCount: planAnalytics.count,
          revenue: revenueAnalytics.revenue,
          subscriptionCount: revenueAnalytics.subscriptionCount || 0
        };
      });
      
      return {
        plans: enhancedPlans,
        analytics: analytics
      };
    } catch (error) {
      console.error('Error fetching platform plans with analytics:', error);
      throw error;
    }
  }

  /**
   * Set a plan as recommended
   * @param {String} planId - The plan ID to set as recommended
   * @returns {Promise<Object>} Result
   */
  async setRecommendedPlan(planId) {
    try {
      const response = await api.post(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/plans/${planId}/recommend`,
        {},
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error setting recommended plan:', error);
      throw error;
    }
  }

  /**
   * Create a new subscription plan
   * @param {Object} planData - The new plan data
   * @returns {Promise<Object>} Promise with created plan
   */
  async createPlan(planData) {
    try {
      console.log('Creating new plan with data:', planData);
      
      const formattedPlanData = {
        name: planData.name,
        description: planData.description,
        price: parseFloat(planData.price),
        interval: planData.interval || 'month',
        features: planData.features || [],
        recommended: planData.isRecommended || false,
        active: planData.active !== false,
        planId: planData.planId
      };
      
      const response = await api.post(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/plans`,
        formattedPlanData,
        this.getAuthHeaders()
      );
      
      console.log('Plan created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating plan:', error);
      if (error.response) {
        console.error('Server responded with:', error.response.status, error.response.data);
      }
      throw error;
    }
  }

  /**
   * Update an existing plan
   * @param {String} planId - The plan ID to update
   * @param {Object} planData - The updated plan data
   * @returns {Promise<Object>} Promise with updated plan
   */
  async updatePlan(planId, planData) {
    try {
      console.log(`Updating plan ${planId} with data:`, planData);
      
      if (!planId) {
        throw new Error('Plan ID is required for updates');
      }
      
      const formattedPlanData = {
        name: planData.name,
        description: planData.description,
        price: parseFloat(planData.price),
        interval: planData.interval || 'month',
        features: planData.features || [],
        recommended: planData.isRecommended || false,
        active: planData.active !== false,
        planId: planData.planId
      };
      
      const response = await api.patch(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/plans/${planId}`,
        formattedPlanData,
        {
          ...this.getAuthHeaders(),
          headers: {
            ...this.getAuthHeaders().headers,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Plan updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error updating plan ${planId}:`, error);
      if (error.response) {
        console.error('Server responded with:', error.response.status, error.response.data);
      }
      throw error;
    }
  }

  /**
   * Delete a plan
   * @param {String} planId - The plan ID to delete
   * @returns {Promise<Object>} Promise with delete confirmation
   */
  async deletePlan(planId) {
    try {
      console.log(`Deleting plan with ID: ${planId}`);
      
      if (!planId) {
        throw new Error('Plan ID is required for deletion');
      }
      
      const response = await api.delete(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/plans/${planId}`,
        this.getAuthHeaders()
      );
      
      console.log('Delete plan response:', response.data);
      
      localStorage.setItem('lastDeletedPlanId', planId);
      localStorage.setItem('lastDeletedTimestamp', Date.now().toString());
      
      return response.data;
    } catch (error) {
      console.error(`Error deleting plan ${planId}:`, error);
      if (error.response) {
        console.error('Server responded with:', error.response.status, error.response.data);
      }
      throw error;
    }
  }

  /**
   * Get details for a specific subscription
   * @param {String} subscriptionId - The subscription ID
   * @returns {Promise<Object>} Promise with subscription details
   */
  async getSubscriptionDetails(subscriptionId) {
    try {
      const response = await api.get(
        `${STRIPE_PREFIX}/platform/subscriptions/${subscriptionId}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching subscription details ${subscriptionId}:`, error);
      throw error;
    }
  }

  /**
   * Update platform-level Stripe configuration
   * @param {Object} config - The Stripe configuration
   * @returns {Promise<Object>} Promise with updated configuration
   */
  async updatePlatformConfig(config) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/platform/config`,
        config,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error updating platform config:', error);
      throw error;
    }
  }

  /**
   * Get platform-level Stripe configuration
   * @returns {Promise<Object>} Promise with platform configuration
   */
  async getPlatformConfig() {
    try {
      const response = await api.get(
        `${STRIPE_PREFIX}/platform/config`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching platform config:', error);
      throw error;
    }
  }

  /**
   * Test Stripe API credentials
   * @param {Object} credentials - The Stripe credentials to test
   * @returns {Promise<Object>} Promise with validation result
   */
  async testStripeCredentials(credentials) {
    try {
      const response = await api.post(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/test-credentials`,
        credentials,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error testing Stripe credentials:', error);
      throw error;
    }
  }

  /**
   * Synchronize platform settings with Stripe
   * @returns {Promise<Object>} Promise with sync results
   */
  async syncWithStripe() {
    try {
      const response = await api.post(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/sync`,
        {},
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error syncing with Stripe:', error);
      throw error;
    }
  }

  /**
   * Get platform webhook events
   * @returns {Promise<Object>} Promise with webhook events
   */
  async getPlatformWebhookEvents() {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/webhook-events`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching webhook events:', error);
      throw error;
    }
  }

  /**
   * Create or update platform webhook
   * @param {Object} webhookData - The webhook data
   * @returns {Promise<Object>} Promise with webhook data
   */
  async updatePlatformWebhook(webhookData) {
    try {
      const response = await api.post(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/webhook`,
        webhookData,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error updating platform webhook:', error);
      throw error;
    }
  }

  /**
   * Get platform fee settings
   * @returns {Promise<Object>} Promise with fee settings
   */
  async getPlatformFeeSettings() {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/fee-settings`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching platform fee settings:', error);
      throw error;
    }
  }

  /**
   * Update platform fee settings
   * @param {Object} feeSettings - The fee settings
   * @returns {Promise<Object>} Promise with updated fee settings
   */
  async updatePlatformFeeSettings(feeSettings) {
    try {
      const response = await api.post(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/fee-settings`,
        feeSettings,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error updating platform fee settings:', error);
      throw error;
    }
  }

  /**
   * Get platform payout settings
   * @returns {Promise<Object>} Promise with payout settings
   */
  async getPlatformPayoutSettings() {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/payout-settings`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching platform payout settings:', error);
      throw error;
    }
  }

  /**
   * Update platform payout settings
   * @param {Object} payoutSettings - The payout settings
   * @returns {Promise<Object>} Promise with updated payout settings
   */
  async updatePlatformPayoutSettings(payoutSettings) {
    try {
      const response = await api.post(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/payout-settings`,
        payoutSettings,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error updating platform payout settings:', error);
      throw error;
    }
  }

  /**
   * Get platform notification settings
   * @returns {Promise<Object>} Promise with notification settings
   */
  async getPlatformNotificationSettings() {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/notification-settings`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching platform notification settings:', error);
      throw error;
    }
  }

  /**
   * Update platform notification settings
   * @param {Object} notificationSettings - The notification settings
   * @returns {Promise<Object>} Promise with updated notification settings
   */
  async updatePlatformNotificationSettings(notificationSettings) {
    try {
      const response = await api.post(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/notification-settings`,
        notificationSettings,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error updating platform notification settings:', error);
      throw error;
    }
  }

  /**
   * Get Stripe account status
   * @returns {Promise<Object>} Promise with account status
   */
  async getStripeAccountStatus() {
    try {
      const response = await api.get(
        `${SUPERADMIN_STRIPE_PREFIX}/platform/account-status`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching Stripe account status:', error);
      throw error;
    }
  }

  /**
   * Generate a Stripe Connect onboarding link for a tenant
   * @param {String} tenantId - The tenant ID
   * @param {String} returnUrl - URL to return to after onboarding
   * @returns {Promise<Object>} Promise with onboarding link
   */
  async generateConnectOnboardingLink(tenantId, returnUrl) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/platform/connect/onboarding`,
        { tenantId, returnUrl },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error generating onboarding link:', error);
      throw error;
    }
  }

  /**
   * Generate reports for platform revenue
   * @param {Object} options - Report options (date range, format, etc)
   * @returns {Promise<Object>} Promise with report data or download URL
   */
  async generateRevenueReport(options) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/platform/reports/revenue`,
        options,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error generating revenue report:', error);
      throw error;
    }
  }

  // =============================================
  // PLATFORM SUBSCRIPTION OPERATIONS
  // =============================================

  /**
   * Create platform customer
   * @param {Object} customerData - Customer data
   * @returns {Promise<Object>} Created customer
   */
  async createPlatformCustomer(customerData) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/platform/customers`,
        customerData,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error creating platform customer:', error);
      throw error;
    }
  }

  /**
   * Create subscription
   * @param {Object} subscriptionData - Subscription data
   * @returns {Promise<Object>} Created subscription
   */
  async createSubscription(subscriptionData) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/platform/subscriptions`,
        subscriptionData,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  /**
   * Update subscription
   * @param {String} subscriptionId - Subscription ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated subscription
   */
  async updateSubscription(subscriptionId, updateData) {
    try {
      const response = await api.patch(
        `${STRIPE_PREFIX}/platform/subscriptions/${subscriptionId}`,
        updateData,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating subscription ${subscriptionId}:`, error);
      throw error;
    }
  }

  /**
   * Create checkout session for subscription payment
   * @param {String} planId - Plan ID
   * @param {String} successUrl - Success URL
   * @param {String} cancelUrl - Cancel URL
   * @param {String} tenantId - Optional tenant ID (uses current tenant if not provided)
   * @param {Object} options - Additional options like adminEmail
   * @returns {Promise<Object>} Checkout session
   */
  async createCheckoutSession(planId, successUrl, cancelUrl, tenantId = null, options = {}) {
    try {
      console.log(`Creating checkout session with planId: ${planId}, tenantId: ${tenantId}`);
      
      if (!planId) throw new Error('Plan ID is required');
      if (!successUrl) throw new Error('Success URL is required');
      if (!cancelUrl) throw new Error('Cancel URL is required');
      
      // Check if planId is a product ID (starts with "prod_") instead of a price ID
      if (planId.startsWith('prod_')) {
        console.log(`Received a product ID (${planId}) instead of a price ID. Attempting to find the corresponding price ID...`);
        
        try {
          // Fetch the available plans to find the one with matching product ID
          const plansResponse = await this.getPlatformPlans();
          
          if (plansResponse && plansResponse.success && Array.isArray(plansResponse.data)) {
            // Try to find the plan with this product ID
            const matchingPlan = plansResponse.data.find(plan => 
              plan.product === planId || plan.productId === planId || plan.id === planId
            );
            
            // If found, use its price ID instead
            if (matchingPlan && matchingPlan.priceId) {
              console.log(`Found matching price ID: ${matchingPlan.priceId} for product: ${planId}`);
              planId = matchingPlan.priceId;
            } else {
              // Try secondary approach - look for a plan with a price object
              const planWithPriceObj = plansResponse.data.find(plan => 
                plan.prices && Array.isArray(plan.prices) && plan.prices.length > 0
              );
              
              if (planWithPriceObj && planWithPriceObj.prices && planWithPriceObj.prices[0].id) {
                console.log(`Found alternative price ID: ${planWithPriceObj.prices[0].id} for product: ${planId}`);
                planId = planWithPriceObj.prices[0].id;
              } else {
                console.warn('Could not find a matching price ID for this product ID. Proceeding with original ID.');
              }
            }
          } else {
            console.warn('Could not fetch plans to resolve product ID to price ID');
          }
        } catch (err) {
          console.error('Error while trying to resolve product ID to price ID:', err);
          // Continue with original ID as a fallback
        }
      }
      
      const requestData = { 
        planId, 
        successUrl, 
        cancelUrl
      };
      
      if (tenantId) {
        requestData.tenantId = tenantId;
      }
      
      // Add admin email if provided in options
      if (options && options.adminEmail) {
        requestData.adminEmail = options.adminEmail;
        console.log(`Including admin email in checkout request: ${options.adminEmail}`);
      }
      
      const headers = this.getAuthHeaders();
      console.log('Request headers:', headers);
      console.log('Request data:', requestData);
      const response = await api.post(
        `${STRIPE_PREFIX}/platform/checkout-sessions`,
        requestData,
        this.getAuthHeaders()
      );
      
      console.log('Checkout session response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      throw error;
    }
  }

  /**
   * Get current tenant subscription details
   * @returns {Promise<Object>} Tenant subscription details
   */
  async getTenantSubscription() {
    try {
      console.log('Fetching tenant subscription details');
      const response = await api.get(
        `${STRIPE_PREFIX}/tenant/subscription`,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      console.log('Tenant subscription response:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tenant subscription: ${error.message}`);
      
      // Handle 404 error gracefully
      if (error.response && error.response.status === 404) {
        console.log('Subscription endpoint not found, returning fallback data');
        
        // Try to get tenant data from localStorage
        try {
          const tenantData = localStorage.getItem('currentTenant');
          if (tenantData) {
            const tenant = JSON.parse(tenantData);
            
            // Return subscription data based on tenant plan
            return {
              success: true,
              data: {
                plan: {
                  name: this.getPlanName(tenant.plan || 'basic'),
                  planId: tenant.plan || 'basic',
                  features: this.getPlanFeatures(tenant.plan || 'basic')
                },
                status: tenant.subscriptionStatus || 'active',
                interval: 'month',
                currentPeriodStart: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
                currentPeriodEnd: tenant.subscriptionEndDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
              }
            };
          }
        } catch (localStorageError) {
          console.warn('Error accessing tenant data from localStorage:', localStorageError);
        }
        
        // Return default data if tenant can't be retrieved
        return {
          success: true,
          data: {
            plan: {
              name: 'Basic Plan',
              planId: 'basic',
              features: ['Standard features']
            },
            status: 'active',
            interval: 'month'
          }
        };
      }
      
      throw error;
    }
  }
  
  /**
   * Get plan name based on plan ID
   * @param {string} planId - Plan ID
   * @returns {string} Plan name
   */
  getPlanName(planId) {
    const planNames = {
      'basic': 'Basic Plan',
      'standard': 'Standard Plan',
      'premium': 'Premium Plan',
      'enterprise': 'Enterprise Plan'
    };
    
    return planNames[planId] || 'Unknown Plan';
  }
  
  /**
   * Get plan features based on plan ID
   * @param {string} planId - Plan ID
   * @returns {Array} Array of features
   */
  getPlanFeatures(planId) {
    const planFeatures = {
      'basic': ['Member Management', 'Email Notifications', 'Basic Analytics'],
      'standard': ['Member Management', 'Email Notifications', 'Standard Analytics', 'Member Portal', 'Payment Processing'],
      'premium': ['Member Management', 'Email Notifications', 'Advanced Analytics', 'Member Portal', 'Payment Processing', 'Custom Branding', 'API Access'],
      'enterprise': ['Member Management', 'Email Notifications', 'Enterprise Analytics', 'Member Portal', 'Payment Processing', 'Custom Branding', 'API Access', 'Dedicated Support', 'Custom Features']
    };
    
    return planFeatures[planId] || ['Basic Features'];
  }

  /**
   * Resume a previously canceled subscription
   * @param {String} subscriptionId - Subscription ID to resume
   * @returns {Promise<Object>} Updated subscription
   */
  async resumeSubscription(subscriptionId) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/tenant/subscriptions/${subscriptionId}/resume`,
        {},
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error(`Error resuming subscription ${subscriptionId}:`, error);
      throw error;
    }
  }

  /**
   * Get tenant payment history
   * @returns {Promise<Object>} Payment history
   */
  async getPaymentHistory() {
    try {
      const response = await api.get(
        `${STRIPE_PREFIX}/tenant/payment-history`,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  }

  /**
   * Get customer payment methods 
   * @returns {Promise<Object>} Payment methods
   */
  async getCustomerPaymentMethods() {
    try {
      const response = await api.get(
        `${STRIPE_PREFIX}/payment-methods`,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error;
    }
  }
  
  /**
   * Add a new payment method
   * @param {Object} paymentMethodData Payment method data
   * @returns {Promise<Object>} Response with the added payment method
   */
  async addPaymentMethod(paymentMethodData) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/payment-methods`,
        paymentMethodData,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error adding payment method:', error);
      throw error;
    }
  }
  
  /**
   * Delete a payment method
   * @param {string} paymentMethodId The payment method ID to delete
   * @returns {Promise<Object>} Response with the delete result
   */
  async deletePaymentMethod(paymentMethodId) {
    try {
      const response = await api.delete(
        `${STRIPE_PREFIX}/payment-methods/${paymentMethodId}`,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting payment method:', error);
      throw error;
    }
  }
  
  /**
   * Update a customer's subscription
   * @param {string} subscriptionId The subscription ID to update
   * @param {Object} updateData The update data
   * @returns {Promise<Object>} Response with the updated subscription
   */
  async updateSubscription(subscriptionId, updateData) {
    try {
      const response = await api.put(
        `${STRIPE_PREFIX}/subscriptions/${subscriptionId}`,
        updateData,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }
  
  /**
   * Cancel a subscription
   * @param {string} subscriptionId The subscription ID to cancel
   * @param {Object} options Cancel options
   * @returns {Promise<Object>} Response with the canceled subscription
   */
  async cancelSubscription(subscriptionId, options = {}) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/subscriptions/${subscriptionId}/cancel`,
        options,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }
  
  /**
   * Get all invoices for the current customer
   * @param {Object} filters Filter options
   * @returns {Promise<Object>} Response with invoices
   */
  async getCustomerInvoices(filters = {}) {
    try {
      const response = await api.get(
        `${STRIPE_PREFIX}/invoices`,
        {
          ...this.getHeadersWithTenant(), // Use headers with tenant
          params: filters
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  }
  
  /**
   * Get an invoice by ID
   * @param {string} invoiceId The invoice ID
   * @returns {Promise<Object>} Response with the invoice details
   */
  async getInvoice(invoiceId) {
    try {
      const response = await api.get(
        `${STRIPE_PREFIX}/invoices/${invoiceId}`,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  }
  
  /**
   * Pay a pending invoice
   * @param {string} invoiceId The invoice ID to pay
   * @returns {Promise<Object>} Response with the paid invoice
   */
  async payInvoice(invoiceId) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/invoices/${invoiceId}/pay`,
        {},
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error paying invoice:', error);
      throw error;
    }
  }
  
  /**
   * Process a payment for a custom amount
   * @param {Object} paymentData Payment data (amount, method, etc)
   * @returns {Promise<Object>} Response with payment result
   */
  async processCustomPayment(paymentData) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/payments/custom`,
        paymentData,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error processing custom payment:', error);
      throw error;
    }
  }

  // =============================================
  // TENANT-SPECIFIC OPERATIONS
  // =============================================

  /**
   * Create tenant customer
   * @param {Object} customerData - Customer data
   * @returns {Promise<Object>} Created customer
   */
  async createTenantCustomer(customerData) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/tenant/customers`,
        customerData,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error creating tenant customer:', error);
      throw error;
    }
  }

  /**
   * Create payment intent
   * @param {Object} paymentData - Payment data
   * @returns {Promise<Object>} Created payment intent
   */
  async createPaymentIntent(paymentData) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/tenant/payment-intents`,
        paymentData,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }

  /**
   * Create invoice
   * @param {Object} invoiceData - Invoice data
   * @returns {Promise<Object>} Created invoice
   */
  async createInvoice(invoiceData) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/tenant/invoices`,
        invoiceData,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  }

  /**
   * Create tenant checkout session
   * @param {Object} paymentData - Payment data
   * @returns {Promise<Object>} Checkout session
   */
  async createTenantCheckoutSession(paymentData) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/tenant/checkout-sessions`,
        paymentData,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error creating tenant checkout session:', error);
      throw error;
    }
  }

  // =============================================
  // CONFIGURATION MANAGEMENT
  // =============================================

  /**
   * Save Stripe configuration
   * @param {Object} configData - Configuration data
   * @returns {Promise<Object>} Saved configuration
   */
  async saveStripeConfig(configData) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/config`,
        configData,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error saving Stripe config:', error);
      throw error;
    }
  }

  /**
   * Get Stripe configuration
   * @returns {Promise<Object>} Stripe configuration
   */
  async getStripeConfig() {
    try {
      const response = await api.get(
        `${STRIPE_PREFIX}/config`,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching Stripe config:', error);
      throw error;
    }
  }

  /**
   * Verify Stripe account credentials
   * @param {Object} credentials - Stripe credentials
   * @returns {Promise<Object>} Verification result
   */
  async verifyStripeCredentials(credentials) {
    try {
      const response = await api.post(
        `${STRIPE_PREFIX}/verify-credentials`,
        credentials,
        this.getHeadersWithTenant() // Use headers with tenant
      );
      return response.data;
    } catch (error) {
      console.error('Error verifying Stripe credentials:', error);
      throw error;
    }
  }

  /**
   * Get subscription plans with cached support
   * This exposes the plans for other components to use
   * @returns {Promise<Array>} Array of subscription plans
   */
  async getSubscriptionPlansWithCache() {
    try {
      // Check if we have cached plans and they're less than 5 minutes old
      const cachedPlansData = localStorage.getItem('subscriptionPlansCache');
      
      if (cachedPlansData) {
        const { timestamp, plans } = JSON.parse(cachedPlansData);
        const now = Date.now();
        
        // If cache is less than 5 minutes old, use it
        if (now - timestamp < 5 * 60 * 1000 && Array.isArray(plans)) {
          console.log('Using cached subscription plans data');
          return plans;
        }
      }
      
      // Fetch fresh data
      console.log('Fetching fresh subscription plans data');
      const response = await this.getPlatformPlans();
      
      // Extract plans from response
      let plansData = [];
      if (response && response.success && Array.isArray(response.data)) {
        plansData = response.data;
        
        // Cache the plans
        localStorage.setItem('subscriptionPlansCache', JSON.stringify({
          timestamp: Date.now(),
          plans: plansData
        }));
      } else if (response && Array.isArray(response.data)) {
        plansData = response.data;
      }
      
      return plansData;
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      
      // Try to get cached data as fallback even if it's older than 5 minutes
      try {
        const cachedPlansData = localStorage.getItem('subscriptionPlansCache');
        if (cachedPlansData) {
          const { plans } = JSON.parse(cachedPlansData);
          if (Array.isArray(plans)) {
            console.log('Using expired cached subscription plans due to error');
            return plans;
          }
        }
      } catch (cacheError) {
        console.warn('Could not retrieve cached plans:', cacheError);
      }
      
      // If all else fails, return default plans
      return this.getDefaultPlans();
    }
  }
}

// Create and export a singleton instance
const stripeService = new StripeService();
export default stripeService;